/**
 * Returns the height in pixels based on the given size.
 *
 * @param { 'xs' | 'sm' | 'md' | 'lg' | 'xl' } size - The size of the element.
 * @return { number } The height in pixels.
 */
const getHeight = (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'): number => {
  switch (size) {
    case 'xs':
      return 24;
    case 'sm':
      return 30;
    case 'md':
      return 36;
    case 'lg':
      return 42;
    case 'xl':
      return 50;
  }
};

/**
 * Returns the CSS border-radius variable based on the given radius value.
 *
 * @param { 'none' | 'sm' | 'md' | 'lg' | 'xl' } radius - The radius value.
 * @return { string } The CSS border-radius variable.
 */
const getRadius = (radius: 'none' | 'sm' | 'md' | 'lg' | 'xl'): string => {
  switch (radius) {
    case 'sm':
      return 'var(--border-radius-xxtiny)';
    case 'md':
      return 'var(--border-radius-xtiny)';
    case 'lg':
      return 'var(--border-radius-xxsmall)';
    case 'xl':
      return 'var(--border-radius-xlarge)';
    case 'none':
      return 'var(--border-radius-none)';
  }
};

/**
 * Returns the size of the icon in pixels based on the given size.
 *
 * @param { 'xs' | 'sm' | 'md' | 'lg' | 'xl' } size - The size of the element.
 * @return { number } The size of the icon in pixels.
 */
const getIconSize = (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'): number => {
  switch (size) {
    case 'xs':
      return 10;
    case 'sm':
      return 14;
    case 'md':
      return 16;
    case 'lg':
      return 18;
    case 'xl':
      return 20;
  }
};

/**
 * Returns the size of the label text in pixels based on the given size.
 *
 * @param { 'xs' | 'sm' | 'md' | 'lg' | 'xl' } size - The size of the element.
 * @return { 'tiny' | 'small' | 'regular' | 'large' } The size of the label text.
 */
const getLabelSize = (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'): 'tiny' | 'small' | 'regular' | 'large' => {
  switch (size) {
    case 'xs':
      return 'tiny';
    case 'sm':
      return 'small';
    case 'md':
      return 'regular';
    case 'lg':
    case 'xl':
      return 'large';
  }
};

/**
 * Returns the CSS padding variable based on the given size.
 *
 * @param { 'xs' | 'sm' | 'md' | 'lg' | 'xl' } size - The size of the element.
 * @return { string } The CSS padding variable.
 */
const getPadding = (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'): string => {
  switch (size) {
    case 'xs':
      return '6px 8px';
    case 'sm':
      return '8px 12px';
    case 'md':
      return '10px 14px';
    case 'lg':
      return '12px 18px';
    case 'xl':
      return '16px 22px';
  }
};

export { getHeight, getIconSize, getLabelSize, getPadding, getRadius };
